import { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import { 
  VideoCamera, Play, Download, Copy, Sparkle, FilmSlate, 
  Clock, Trash, Lightning, Target, Rocket, CircleNotch,
  CheckCircle, FilmStrip
} from '@phosphor-icons/react';
import axios from 'axios';
import { toast } from 'sonner';
import AppLayout from '@/components/AppLayout';
import BackButton from '@/components/BackButton';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Progress } from '@/components/ui/progress';
import { useTheme } from '@/contexts/ThemeContext';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// Templates vidéo prédéfinis
const VIDEO_TEMPLATES = [
  {
    value: 'custom',
    label: 'Personnalisé',
    emoji: '✏️',
    description: 'Créez votre propre vidéo',
    topic: '',
    style: 'dynamique',
    structure: null
  },
  {
    value: 'testimonial',
    label: 'Témoignage client',
    emoji: '🎤',
    description: 'Avant/Après avec résultat concret',
    topic: 'Témoignage client satisfait - Transformation et résultat',
    style: 'storytelling',
    structure: {
      hook: 'Avant j\'avais ce problème...',
      body: 'Comment le produit/service a changé ma vie',
      cta: 'Vous aussi, essayez maintenant !'
    }
  },
  {
    value: 'product',
    label: 'Présentation produit',
    emoji: '🚀',
    description: 'Fonctionnalités et avantages',
    topic: 'Présentation produit - Fonctionnalités et bénéfices',
    style: 'professionnel',
    structure: {
      hook: 'Découvrez [produit] !',
      body: 'Fonctionnalités clés et avantages uniques',
      cta: 'Commandez maintenant !'
    }
  },
  {
    value: 'promo',
    label: 'Offre promotionnelle',
    emoji: '⚡',
    description: 'Urgence et call-to-action fort',
    topic: 'Offre flash limitée - Promotion exceptionnelle',
    style: 'dynamique',
    structure: {
      hook: '-50% AUJOURD\'HUI SEULEMENT !',
      body: 'Offre exclusive, quantités limitées',
      cta: 'Achetez MAINTENANT avant rupture !'
    }
  }
];

const STYLES = [
  { value: 'dynamique', label: 'Dynamique', emoji: '⚡', desc: 'Rythme rapide, énergique' },
  { value: 'professionnel', label: 'Professionnel', emoji: '💼', desc: 'Sobre et crédible' },
  { value: 'storytelling', label: 'Storytelling', emoji: '📖', desc: 'Narratif et émotionnel' },
  { value: 'humoristique', label: 'Humoristique', emoji: '😄', desc: 'Fun et léger' },
];

const DURATIONS = [
  { value: '15s', label: '15 secondes', scenes: 4, desc: 'Stories • 4 scènes' },
  { value: '30s', label: '30 secondes', scenes: 7, desc: 'Reels/TikTok • 7 scènes' },
  { value: '40s', label: '40 secondes', scenes: 10, desc: 'YouTube Shorts • 10 scènes' },
];

const PLATFORMS = [
  { value: 'reels', label: 'Instagram Reels', emoji: '📸' },
  { value: 'tiktok', label: 'TikTok', emoji: '🎵' },
  { value: 'youtube_shorts', label: 'YouTube Shorts', emoji: '▶️' },
];

// Placeholders dynamiques
const PLACEHOLDER_EXAMPLES = [
  'Plombier Paris urgence',
  'Coach fitness perte de poids',
  'Coiffeur balayage Lyon',
  'Agent immobilier Marseille',
  'Photographe mariage haut de gamme',
  'Électricien dépannage rapide',
];

export default function Video() {
  const { t } = useTranslation();
  const { isDark } = useTheme();
  
  // Form state
  const [template, setTemplate] = useState('custom');
  const [topic, setTopic] = useState('');
  const [style, setStyle] = useState('dynamique');
  const [duration, setDuration] = useState('30s');
  const [platform, setPlatform] = useState('reels');
  
  // Generation state
  const [loading, setLoading] = useState(false);
  const [generatingVideoId, setGeneratingVideoId] = useState(null);
  const [generationStatus, setGenerationStatus] = useState(null);
  const [generatedVideo, setGeneratedVideo] = useState(null);
  
  // History state
  const [videos, setVideos] = useState([]);
  const [showHistory, setShowHistory] = useState(false);
  const [stats, setStats] = useState(null);
  
  // UI state
  const [placeholderIndex, setPlaceholderIndex] = useState(0);

  // Rotation des placeholders
  useEffect(() => {
    const interval = setInterval(() => {
      setPlaceholderIndex((prev) => (prev + 1) % PLACEHOLDER_EXAMPLES.length);
    }, 3000);
    return () => clearInterval(interval);
  }, []);

  // Charger l'historique et les stats
  useEffect(() => {
    fetchVideos();
    fetchStats();
    
    const handleRefresh = () => {
      fetchVideos();
      fetchStats();
    };
    window.addEventListener('pulltorefresh', handleRefresh);
    return () => window.removeEventListener('pulltorefresh', handleRefresh);
  }, []);

  // Polling du statut de génération
  useEffect(() => {
    let interval;
    if (generatingVideoId) {
      interval = setInterval(async () => {
        try {
          const response = await axios.get(
            `${BACKEND_URL}/api/video/generate/status/${generatingVideoId}`,
            { withCredentials: true }
          );
          setGenerationStatus(response.data);
          
          if (response.data.status === 'completed') {
            setGeneratedVideo(response.data);
            setGeneratingVideoId(null);
            setLoading(false);
            toast.success('🎬 Votre vidéo est prête !');
            fetchVideos();
            fetchStats();
          } else if (response.data.status === 'failed') {
            setGeneratingVideoId(null);
            setLoading(false);
            toast.error(response.data.message || 'Erreur lors de la génération');
          }
        } catch (error) {
          console.error('Error checking status:', error);
        }
      }, 5000); // Vérifier toutes les 5 secondes
    }
    return () => clearInterval(interval);
  }, [generatingVideoId]);

  const fetchVideos = async () => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/video/generated`, {
        withCredentials: true,
      });
      setVideos(response.data.videos);
    } catch (error) {
      console.error('Error fetching videos:', error);
    }
  };

  const fetchStats = async () => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/video/generated/stats`, {
        withCredentials: true,
      });
      setStats(response.data);
    } catch (error) {
      console.error('Error fetching stats:', error);
    }
  };

  const generateVideo = async () => {
    if (!topic.trim()) {
      toast.error('Décrivez le sujet de votre vidéo');
      return;
    }

    setLoading(true);
    setGenerationStatus(null);
    setGeneratedVideo(null);

    try {
      const response = await axios.post(
        `${BACKEND_URL}/api/video/generate`,
        { topic, style, duration, platform },
        { withCredentials: true }
      );
      
      setGeneratingVideoId(response.data.video_id);
      setGenerationStatus({
        status: 'generating',
        progress: 0,
        message: response.data.message,
        total_scenes: response.data.total_scenes
      });
      
      toast.info(`🎬 Génération lancée ! Environ ${response.data.estimated_time}`);
      
    } catch (error) {
      setLoading(false);
      toast.error(error.response?.data?.detail || 'Erreur lors de la génération');
    }
  };

  const downloadVideo = async (videoId, title) => {
    try {
      const response = await axios.get(
        `${BACKEND_URL}/api/video/download/${videoId}`,
        { 
          withCredentials: true,
          responseType: 'blob'
        }
      );
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `${title || 'video'}_${videoId}.mp4`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      toast.success('Vidéo téléchargée !');
    } catch (error) {
      toast.error('Erreur lors du téléchargement');
    }
  };

  const deleteVideo = async (videoId) => {
    try {
      await axios.delete(`${BACKEND_URL}/api/video/generated/${videoId}`, {
        withCredentials: true,
      });
      toast.success('Vidéo supprimée');
      fetchVideos();
      fetchStats();
    } catch (error) {
      toast.error('Erreur lors de la suppression');
    }
  };

  // Gestion de la sélection de template
  const handleTemplateSelect = (templateValue) => {
    setTemplate(templateValue);
    const selectedTemplate = VIDEO_TEMPLATES.find(t => t.value === templateValue);
    if (selectedTemplate && templateValue !== 'custom') {
      setTopic(selectedTemplate.topic);
      setStyle(selectedTemplate.style);
      toast.success(`Template "${selectedTemplate.label}" appliqué !`);
    }
  };

  const selectedDuration = DURATIONS.find(d => d.value === duration);
  const selectedTemplate = VIDEO_TEMPLATES.find(t => t.value === template);

  return (
    <AppLayout>
      <div className="p-4 md:p-8 max-w-7xl mx-auto relative z-10" data-testid="video-page">
        {/* Back Button */}
        <BackButton to="/dashboard" label="Retour au tableau de bord" className="mb-4" />
        
        {/* Header */}
        <motion.div
          initial={{ y: 20, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          className="mb-8"
        >
          {/* Badge */}
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            className={`inline-flex items-center gap-2 px-4 py-2 rounded-full mb-4 ${
              isDark 
                ? 'bg-gradient-to-r from-violet-500/10 to-pink-500/10 border border-violet-500/20' 
                : 'bg-violet-50 border border-violet-100'
            }`}
          >
            <FilmSlate className="text-violet-500" size={16} weight="fill" />
            <span className={`text-sm font-medium ${isDark ? 'text-violet-400' : 'text-violet-600'}`}>
              Génération vidéo IA • Sora 2
            </span>
          </motion.div>

          <div className="flex items-center justify-between flex-wrap gap-4">
            <div>
              <h1 className={`text-4xl md:text-5xl lg:text-6xl font-extrabold tracking-tight mb-3 ${isDark ? 'text-white' : 'text-gray-900'}`}>
                Créez des vidéos
                <span className="block gradient-text">qui convertissent</span>
              </h1>
              <p className={`text-lg md:text-xl ${isDark ? 'text-white/50' : 'text-gray-500'}`}>
                Vidéos TikTok, Reels et Shorts générées automatiquement par l'IA
              </p>
            </div>
            {stats && (
              <div className="flex items-center gap-4">
                <div className={`px-6 py-4 rounded-2xl text-center ${isDark ? 'glass-card' : 'bg-white border border-gray-100 shadow-sm'}`}>
                  <p className="text-3xl font-bold gradient-text">{stats.total_videos}</p>
                  <p className={`text-sm ${isDark ? 'text-white/40' : 'text-gray-500'}`}>Vidéos créées</p>
                </div>
                <div className={`px-6 py-4 rounded-2xl text-center ${isDark ? 'glass-card' : 'bg-white border border-gray-100 shadow-sm'}`}>
                  <p className="text-3xl font-bold gradient-text">{stats.total_duration_formatted}</p>
                  <p className={`text-sm ${isDark ? 'text-white/40' : 'text-gray-500'}`}>Durée totale</p>
                </div>
              </div>
            )}
          </div>
        </motion.div>

        <div className="grid lg:grid-cols-2 gap-8">
          {/* Formulaire de génération */}
          <motion.div
            initial={{ y: 20, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            transition={{ delay: 0.1 }}
            className={`p-6 rounded-2xl ${isDark ? 'glass-card' : 'bg-white border border-gray-100 shadow-sm'}`}
          >
            <h2 className={`text-xl font-semibold mb-6 flex items-center gap-3 ${isDark ? 'text-white/90' : 'text-gray-900'}`}>
              <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${isDark ? 'bg-gradient-to-br from-violet-500/20 to-pink-500/20' : 'bg-violet-100'}`}>
                <Sparkle className="text-violet-500" weight="duotone" size={20} />
              </div>
              Créer votre vidéo
            </h2>

            <div className="space-y-5">
              {/* Templates rapides */}
              <div>
                <Label className={`text-base font-medium mb-3 block ${isDark ? 'text-white/80' : 'text-gray-700'}`}>
                  Template rapide
                </Label>
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                  {VIDEO_TEMPLATES.map((t) => (
                    <button
                      key={t.value}
                      onClick={() => handleTemplateSelect(t.value)}
                      disabled={loading}
                      className={`p-3 rounded-xl text-center transition-all ${
                        template === t.value
                          ? isDark
                            ? 'bg-violet-500/20 border-2 border-violet-500 text-white'
                            : 'bg-violet-100 border-2 border-violet-500 text-violet-700'
                          : isDark
                            ? 'bg-white/5 border border-white/10 text-white/70 hover:bg-white/10 hover:border-white/20'
                            : 'bg-gray-50 border border-gray-200 text-gray-600 hover:bg-gray-100 hover:border-gray-300'
                      } ${loading ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}`}
                      data-testid={`template-${t.value}`}
                    >
                      <span className="text-xl block mb-1">{t.emoji}</span>
                      <span className="text-xs font-medium block">{t.label}</span>
                    </button>
                  ))}
                </div>
                {selectedTemplate && selectedTemplate.value !== 'custom' && (
                  <p className={`text-xs mt-2 ${isDark ? 'text-violet-400' : 'text-violet-600'}`}>
                    ✨ {selectedTemplate.description}
                  </p>
                )}
              </div>

              {/* Sujet */}
              <div>
                <Label htmlFor="topic" className={`text-base font-medium ${isDark ? 'text-white/80' : 'text-gray-700'}`}>
                  Sujet de la vidéo
                </Label>
                <Input
                  id="topic"
                  placeholder={template === 'custom' ? `Ex: ${PLACEHOLDER_EXAMPLES[placeholderIndex]}` : 'Personnalisez le sujet...'}
                  value={topic}
                  onChange={(e) => {
                    setTopic(e.target.value);
                    if (template !== 'custom') setTemplate('custom');
                  }}
                  className={`mt-2 h-12 text-base ${isDark ? 'bg-white/[0.03] border-white/[0.08]' : ''}`}
                  data-testid="video-topic-input"
                  disabled={loading}
                />
                <p className={`text-xs mt-2 ${isDark ? 'text-white/40' : 'text-gray-500'}`}>
                  {template !== 'custom' ? 'Modifiez le sujet pré-rempli selon vos besoins' : 'Décrivez votre activité, produit ou service à promouvoir'}
                </p>
              </div>

              {/* Style et Durée */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className={isDark ? 'text-white/70' : 'text-gray-700'}>Style</Label>
                  <Select value={style} onValueChange={setStyle} disabled={loading}>
                    <SelectTrigger className={`mt-1 ${isDark ? 'bg-white/[0.03] border-white/[0.08]' : ''}`} data-testid="video-style-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {STYLES.map((s) => (
                        <SelectItem key={s.value} value={s.value}>
                          <span className="flex items-center gap-2">
                            <span>{s.emoji}</span>
                            <span>{s.label}</span>
                          </span>
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div>
                  <Label className={isDark ? 'text-white/70' : 'text-gray-700'}>Durée</Label>
                  <Select value={duration} onValueChange={setDuration} disabled={loading}>
                    <SelectTrigger className={`mt-1 ${isDark ? 'bg-white/[0.03] border-white/[0.08]' : ''}`} data-testid="video-duration-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {DURATIONS.map((d) => (
                        <SelectItem key={d.value} value={d.value}>
                          <span className="flex items-center gap-2">
                            <Clock size={14} />
                            <span>{d.label}</span>
                          </span>
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              {/* Plateforme */}
              <div>
                <Label className={isDark ? 'text-white/70' : 'text-gray-700'}>Plateforme</Label>
                <Select value={platform} onValueChange={setPlatform} disabled={loading}>
                  <SelectTrigger className={`mt-1 ${isDark ? 'bg-white/[0.03] border-white/[0.08]' : ''}`} data-testid="video-platform-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {PLATFORMS.map((p) => (
                      <SelectItem key={p.value} value={p.value}>
                        {p.emoji} {p.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {/* Info sur la durée sélectionnée */}
              {selectedDuration && (
                <div className={`p-4 rounded-xl ${isDark ? 'bg-violet-500/10 border border-violet-500/20' : 'bg-violet-50 border border-violet-100'}`}>
                  <div className="flex items-center gap-3">
                    <FilmStrip className="text-violet-500" size={20} weight="duotone" />
                    <div>
                      <p className={`text-sm font-medium ${isDark ? 'text-violet-400' : 'text-violet-600'}`}>
                        {selectedDuration.scenes} scènes de 4 secondes
                      </p>
                      <p className={`text-xs ${isDark ? 'text-white/50' : 'text-gray-500'}`}>
                        Durée totale : ~{selectedDuration.scenes * 4}s • Format vertical 9:16
                      </p>
                    </div>
                  </div>
                </div>
              )}

              {/* Bouton de génération */}
              <div className="pt-2">
                <Button
                  onClick={generateVideo}
                  disabled={loading || !topic.trim()}
                  className="w-full h-14 text-base btn-premium"
                  data-testid="generate-video-btn"
                >
                  {loading ? (
                    <>
                      <CircleNotch className="animate-spin mr-2" size={20} />
                      Génération en cours...
                    </>
                  ) : (
                    <>
                      <VideoCamera className="mr-2" weight="duotone" size={20} />
                      Générer ma vidéo
                    </>
                  )}
                </Button>
                
                <p className={`text-center text-xs mt-3 ${isDark ? 'text-white/40' : 'text-gray-500'}`}>
                  ⏱️ Temps estimé : {selectedDuration ? `${selectedDuration.scenes * 2}-${selectedDuration.scenes * 4}` : '10-20'} minutes
                </p>
              </div>
            </div>

            {/* Historique */}
            <div className={`mt-6 pt-6 border-t ${isDark ? 'border-white/10' : 'border-gray-100'}`}>
              <button
                onClick={() => setShowHistory(!showHistory)}
                className={`flex items-center justify-between w-full text-sm transition-colors ${isDark ? 'text-white/50 hover:text-white/70' : 'text-gray-500 hover:text-gray-700'}`}
              >
                <span>Mes vidéos ({videos.length})</span>
                <span>{showHistory ? '▲' : '▼'}</span>
              </button>

              <AnimatePresence>
                {showHistory && videos.length > 0 && (
                  <motion.div
                    initial={{ height: 0, opacity: 0 }}
                    animate={{ height: 'auto', opacity: 1 }}
                    exit={{ height: 0, opacity: 0 }}
                    className="mt-4 space-y-2 max-h-60 overflow-y-auto"
                  >
                    {videos.slice(0, 10).map((video) => (
                      <div
                        key={video.video_id}
                        className={`flex items-center justify-between p-3 rounded-lg transition-colors ${
                          isDark ? 'bg-white/5 hover:bg-white/10' : 'bg-gray-50 hover:bg-gray-100'
                        }`}
                      >
                        <div className="flex-1 min-w-0">
                          <p className={`text-sm font-medium truncate ${isDark ? 'text-white' : 'text-gray-900'}`}>
                            {video.video_title || video.topic}
                          </p>
                          <p className={`text-xs ${isDark ? 'text-white/50' : 'text-gray-500'}`}>
                            {video.platform} • {video.total_duration}s • {video.scenes_count} scènes
                          </p>
                        </div>
                        <div className="flex items-center gap-2">
                          <button
                            onClick={() => downloadVideo(video.video_id, video.video_title)}
                            className={`p-2 rounded-lg transition-colors ${isDark ? 'hover:bg-white/10 text-white/60 hover:text-green-400' : 'hover:bg-gray-200 text-gray-500 hover:text-green-600'}`}
                            title="Télécharger"
                          >
                            <Download size={16} />
                          </button>
                          <button
                            onClick={() => deleteVideo(video.video_id)}
                            className={`p-2 rounded-lg transition-colors ${isDark ? 'hover:bg-white/10 text-white/60 hover:text-red-400' : 'hover:bg-gray-200 text-gray-500 hover:text-red-600'}`}
                            title="Supprimer"
                          >
                            <Trash size={16} />
                          </button>
                        </div>
                      </div>
                    ))}
                  </motion.div>
                )}
              </AnimatePresence>
            </div>
          </motion.div>

          {/* Zone de résultat */}
          <motion.div
            initial={{ y: 20, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            transition={{ delay: 0.2 }}
            className={`p-6 rounded-2xl ${isDark ? 'glass-card' : 'bg-white border border-gray-100 shadow-sm'}`}
          >
            {/* En cours de génération */}
            {loading && generationStatus && (
              <div className="h-full min-h-[500px] flex flex-col items-center justify-center text-center px-4">
                <div className={`w-24 h-24 rounded-3xl flex items-center justify-center mb-6 ${isDark ? 'bg-gradient-to-br from-violet-500/20 to-pink-500/20' : 'bg-gradient-to-br from-violet-100 to-pink-100'}`}>
                  <CircleNotch size={48} weight="bold" className="text-violet-500 animate-spin" />
                </div>
                
                <h3 className={`text-xl font-semibold mb-3 ${isDark ? 'text-white/90' : 'text-gray-900'}`}>
                  Génération en cours...
                </h3>
                
                <p className={`mb-6 ${isDark ? 'text-white/50' : 'text-gray-600'}`}>
                  {generationStatus.message}
                </p>
                
                <div className="w-full max-w-xs mb-4">
                  <Progress value={generationStatus.progress || 0} className="h-3" />
                </div>
                
                <p className={`text-sm ${isDark ? 'text-white/40' : 'text-gray-500'}`}>
                  {generationStatus.progress || 0}% • Scène {generationStatus.current_scene || 0}/{generationStatus.total_scenes || '?'}
                </p>
                
                <div className={`mt-6 p-4 rounded-xl ${isDark ? 'bg-amber-500/10 border border-amber-500/20' : 'bg-amber-50 border border-amber-100'}`}>
                  <p className={`text-sm ${isDark ? 'text-amber-400' : 'text-amber-600'}`}>
                    💡 La génération peut prendre quelques minutes. Vous pouvez quitter cette page, votre vidéo sera disponible dans l'historique.
                  </p>
                </div>
              </div>
            )}

            {/* Vidéo générée */}
            {!loading && generatedVideo && generatedVideo.status === 'completed' && (
              <div className="space-y-6">
                <div className="flex items-center justify-between">
                  <h2 className={`text-xl font-semibold flex items-center gap-3 ${isDark ? 'text-white/90' : 'text-gray-900'}`}>
                    <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${isDark ? 'bg-gradient-to-br from-green-500/20 to-emerald-500/20' : 'bg-green-100'}`}>
                      <CheckCircle className="text-green-500" weight="fill" size={20} />
                    </div>
                    Vidéo prête !
                  </h2>
                </div>

                {/* Prévisualisation */}
                <div className={`aspect-[9/16] max-h-[400px] mx-auto rounded-2xl overflow-hidden ${isDark ? 'bg-black/50' : 'bg-gray-900'}`}>
                  <video 
                    className="w-full h-full object-contain"
                    controls
                    src={`${BACKEND_URL}${generatedVideo.video_url}`}
                  >
                    Votre navigateur ne supporte pas la vidéo.
                  </video>
                </div>

                {/* Infos */}
                <div className="flex gap-2 text-xs flex-wrap justify-center">
                  <span className={`px-3 py-1.5 rounded-full ${isDark ? 'bg-violet-500/15 text-violet-400 border border-violet-500/20' : 'bg-violet-100 text-violet-600'}`}>
                    {generatedVideo.total_duration}s
                  </span>
                  <span className={`px-3 py-1.5 rounded-full ${isDark ? 'bg-pink-500/15 text-pink-400 border border-pink-500/20' : 'bg-pink-100 text-pink-600'}`}>
                    {generatedVideo.scenes_count} scènes
                  </span>
                  <span className={`px-3 py-1.5 rounded-full ${isDark ? 'bg-white/[0.06] text-white/60' : 'bg-gray-100 text-gray-600'}`}>
                    Format 9:16
                  </span>
                </div>

                {/* Bouton téléchargement */}
                <Button
                  onClick={() => downloadVideo(generatedVideo.video_id, generatedVideo.video_title)}
                  className="w-full h-12 btn-premium"
                >
                  <Download className="mr-2" size={18} />
                  Télécharger ma vidéo
                </Button>

                {/* Conseil */}
                <div className={`p-4 rounded-xl text-center ${isDark ? 'bg-gradient-to-r from-violet-500/5 to-pink-500/5 border border-violet-500/10' : 'bg-gradient-to-r from-violet-50 to-pink-50 border border-violet-100'}`}>
                  <p className={`text-sm ${isDark ? 'text-white/70' : 'text-gray-600'}`}>
                    💡 <strong>Conseil :</strong> Ajoutez de la musique avec CapCut ou InShot avant de publier !
                  </p>
                </div>
              </div>
            )}

            {/* État vide */}
            {!loading && !generatedVideo && (
              <div className="h-full min-h-[500px] flex flex-col items-center justify-center text-center px-4">
                <div className={`w-20 h-20 rounded-2xl flex items-center justify-center mb-6 ${isDark ? 'bg-gradient-to-br from-violet-500/15 to-pink-500/15' : 'bg-gradient-to-br from-violet-100 to-pink-100'}`}>
                  <VideoCamera size={40} weight="duotone" className="text-violet-500" />
                </div>
                
                <h3 className={`text-xl font-semibold mb-3 ${isDark ? 'text-white/90' : 'text-gray-900'}`}>
                  Créez des vidéos marketing
                </h3>
                
                <p className={`max-w-sm mb-6 ${isDark ? 'text-white/50' : 'text-gray-600'}`}>
                  Générez des vidéos professionnelles en quelques clics pour TikTok, Reels et YouTube Shorts.
                </p>
                
                {/* Points forts */}
                <div className={`p-4 rounded-xl text-left w-full max-w-sm ${isDark ? 'bg-white/5' : 'bg-gray-50'}`}>
                  <p className={`text-xs font-semibold mb-3 ${isDark ? 'text-white/60' : 'text-gray-500'}`}>
                    AVANTAGES :
                  </p>
                  <ul className={`text-sm space-y-2 ${isDark ? 'text-white/60' : 'text-gray-600'}`}>
                    <li className="flex items-center gap-2">
                      <Lightning className="text-amber-500" size={16} weight="fill" />
                      Hook percutant en première scène
                    </li>
                    <li className="flex items-center gap-2">
                      <Target className="text-green-500" size={16} weight="fill" />
                      Call-to-action optimisé
                    </li>
                    <li className="flex items-center gap-2">
                      <Rocket className="text-violet-500" size={16} weight="fill" />
                      Format vertical 9:16 natif
                    </li>
                  </ul>
                </div>
              </div>
            )}
          </motion.div>
        </div>
      </div>
    </AppLayout>
  );
}
